/**
 * 緯度経度 ⇔ ローカルENU座標の変換。
 * 原点近傍(数百m)の平面近似で十分(計画書§7.1)。
 *
 * ワールド座標規約(付録A): +X=東, +Y=上, +Z=南 (north = -Z)
 */

export interface GeoPoint {
  lat: number;
  lon: number;
}

export interface EnuOffset {
  east: number;
  north: number;
}

const METERS_PER_DEG_LAT = 110540;
const METERS_PER_DEG_LON_EQUATOR = 111320;

const DEG2RAD = Math.PI / 180;

/** 原点からのENUオフセット[m]を返す */
export function enuFromGeodetic(origin: GeoPoint, p: GeoPoint): EnuOffset {
  const east = (p.lon - origin.lon) * Math.cos(origin.lat * DEG2RAD) * METERS_PER_DEG_LON_EQUATOR;
  const north = (p.lat - origin.lat) * METERS_PER_DEG_LAT;
  return { east, north };
}

/** 2点間の距離[m](平面近似) */
export function distanceMeters(a: GeoPoint, b: GeoPoint): number {
  const { east, north } = enuFromGeodetic(a, b);
  return Math.hypot(east, north);
}

/** aからbへの方位角[deg]。0=真北, 90=東(時計回り) */
export function bearingDeg(a: GeoPoint, b: GeoPoint): number {
  const { east, north } = enuFromGeodetic(a, b);
  return normalizeDeg((Math.atan2(east, north) * 180) / Math.PI);
}

/** 角度を[0, 360)に正規化 */
export function normalizeDeg(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/** 2つの方位角の差を(-180, 180]で返す */
export function angleDiffDeg(a: number, b: number): number {
  let d = normalizeDeg(a - b);
  if (d > 180) d -= 360;
  return d;
}

/**
 * 磁北→真北の補正。
 * declinationDeg は東偏を正とする(日本は西偏なので約 -7〜-8)。
 * trueHeading = magneticHeading + declinationDeg
 */
export function trueHeadingDeg(magneticHeadingDeg: number, declinationDeg: number): number {
  return normalizeDeg(magneticHeadingDeg + declinationDeg);
}
